import React, { useState, useEffect } from 'react';
import { Cookie, ShieldCheck, X } from 'lucide-react';

interface CookieBannerProps {
  onOpenPrivacy: () => void;
}

export const CookieBanner: React.FC<CookieBannerProps> = ({ onOpenPrivacy }) => {
  const [isVisible, setIsVisible] = useState(false);

  useEffect(() => {
    const consent = localStorage.getItem('ck_cookie_consent');
    if (!consent) {
      // Show banner after brief delay
      const timer = setTimeout(() => setIsVisible(true), 1200);
      return () => clearTimeout(timer);
    }
  }, []);

  const handleAccept = () => {
    localStorage.setItem('ck_cookie_consent', 'accepted');
    setIsVisible(false);
  };

  const handleEssentialOnly = () => {
    localStorage.setItem('ck_cookie_consent', 'essential');
    setIsVisible(false);
  };

  if (!isVisible) return null;

  return (
    <div className="fixed bottom-4 left-4 right-4 md:left-auto md:right-6 md:max-w-md z-40 animate-slideUp">
      <div className="p-4 rounded-2xl bg-[#0f111a]/95 border border-cyan-500/30 backdrop-blur-xl shadow-[0_10px_35px_rgba(0,0,0,0.8),0_0_20px_rgba(0,240,255,0.15)] text-gray-300 flex flex-col gap-3">
        
        <div className="flex items-start justify-between gap-3">
          <div className="flex items-center gap-2.5">
            <div className="p-2 rounded-xl bg-cyan-500/10 border border-cyan-500/30 text-cyan-400">
              <Cookie size={18} />
            </div>
            <div>
              <h4 className="text-xs font-bold text-white flex items-center gap-1.5">
                Privacy &amp; Local Storage
                <span className="px-1.5 py-0.2 text-[9px] font-semibold text-emerald-400 bg-emerald-500/10 border border-emerald-500/30 rounded-full">
                  Zero Trackers
                </span>
              </h4>
              <p className="text-[11px] text-gray-400">No ads, no analytics, no 3rd-party cookies.</p>
            </div>
          </div>
          <button
            onClick={handleEssentialOnly}
            className="p-1 text-gray-400 hover:text-white rounded-lg hover:bg-white/5 transition-colors"
          >
            <X size={15} />
          </button>
        </div>

        <p className="text-[11px] text-gray-400 leading-relaxed">
          We use strictly essential browser local storage to save your encrypted room credentials and user preferences. All clipboard sync is end-to-end encrypted with AES-256.
        </p>

        <div className="flex items-center justify-between gap-2 pt-1">
          <button
            onClick={onOpenPrivacy}
            className="text-[11px] text-cyan-400 hover:underline font-medium"
          >
            Privacy Policy &amp; Terms
          </button>
          <div className="flex items-center gap-2">
            <button
              onClick={handleEssentialOnly}
              className="px-3 py-1.5 text-[11px] font-medium text-gray-300 hover:text-white bg-white/5 hover:bg-white/10 rounded-xl transition-colors"
            >
              Essential Only
            </button>
            <button
              onClick={handleAccept}
              className="px-3.5 py-1.5 text-[11px] font-semibold text-black bg-cyan-400 hover:bg-cyan-300 rounded-xl shadow-[0_0_15px_rgba(0,240,255,0.3)] transition-all"
            >
              Accept All
            </button>
          </div>
        </div>

      </div>
    </div>
  );
};
